import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, InputAdornment, Checkbox, Chip, Dialog,
  DialogContent, IconButton, Tooltip, CircularProgress, Menu, MenuItem, Snackbar,
  Alert, Divider, alpha, ToggleButtonGroup, ToggleButton, Avatar,
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon, Upload as UploadIcon,
  Image as ImageIcon, Movie as MovieIcon, TextFields as TextIcon,
  DeleteOutline as DeleteIcon, Close as CloseIcon, ChatBubbleOutline as ChatIcon,
  FolderOpen as FolderIcon, EditOutlined as EditIcon,
  AddPhotoAlternate as AddPhotoIcon, AutoAwesome as VarIcon,
} from '@mui/icons-material';
import { designTokens as t } from '../theme';
import { getToken } from '../api';
import MediaPicker from '../components/MediaPicker';
import ThumbStack from '../components/ThumbStack';

const API = process.env.REACT_APP_API_URL;

// Variables dynamiques insérables dans une légende. Résolues côté backend au
// moment de la publication (resolvePostVariables).
const VARIABLES = [
  { key: 'username', label: 'username' },
  { key: 'name', label: 'name' },
  { key: 'day_en', label: 'day_en' },
  { key: 'day_fr', label: 'day_fr' },
  { key: 'p_firstname', label: 'p_firstname' },
  { key: 'p_age', label: 'p_age' },
  { key: 'p_origin', label: 'p_origin' },
  { key: 'p_location', label: 'p_location' },
  { key: 'p_occupation', label: 'p_occupation' },
];

const MAX_CHARS = 500;

// ---- helpers ------------------------------------------------------------
function mediaTypeOf(post) {
  if (post.mediaType) return post.mediaType;
  if (Array.isArray(post.imageUploadIds) && post.imageUploadIds.length) return 'image';
  if (post.videoData) return 'video';
  return 'text';
}

function thumbUrl(post) {
  const ids = post.imageUploadIds || post.uploadIds;
  if (Array.isArray(ids) && ids.length) {
    return `${API}/api/uploads/${ids[0]}/raw?token=${getToken()}`;
  }
  if (post.videoData?.uploadId) {
    return `${API}/api/uploads/${post.videoData.uploadId}/raw?token=${getToken()}`;
  }
  return null;
}

// Toutes les images/vidéos d'un post (pour l'aperçu).
function mediaUrls(post) {
  const ids = post.imageUploadIds || post.uploadIds || [];
  if (Array.isArray(ids) && ids.length) {
    return ids.map((id) => ({ url: `${API}/api/uploads/${id}/raw?token=${getToken()}`, video: false }));
  }
  if (post.videoData?.uploadId) {
    return [{ url: `${API}/api/uploads/${post.videoData.uploadId}/raw?token=${getToken()}`, video: true }];
  }
  return [];
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

// ---- group chip ---------------------------------------------------------
function GroupChip({ group }) {
  if (!group) {
    return <Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>—</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: group.color || t.primary, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '0.8rem', color: group.color || t.foreground, fontWeight: 500 }}>
        {group.name}
      </Typography>
    </Box>
  );
}

export default function PostLibrary() {
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState({});       // { id: {name,color,...} }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignAnchor, setAssignAnchor] = useState(null);
  const [preview, setPreview] = useState(null);    // selected post
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const csvInputRef = useRef(null);

  const notify = (msg, severity = 'success') => setToast({ open: true, msg, severity });

  const groupOf = useCallback((post) => {
    const id = post.profile;
    if (!id) return null;
    const g = groups[id];
    return { id, name: g?.name || id, color: g?.color };
  }, [groups]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [draftsRes, cfgRes] = await Promise.all([
        axios.get(`${API}/api/drafts`),
        axios.get(`${API}/api/profiles/config`).catch(() => ({ data: { profiles: {} } })),
      ]);
      setPosts(Array.isArray(draftsRes.data?.drafts) ? draftsRes.data.drafts : []);
      setGroups(cfgRes.data?.profiles || {});
    } catch (e) {
      notify(e.response?.data?.error || 'Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // counts per group for the tabs
  const groupCounts = useMemo(() => {
    const m = {};
    posts.forEach((p) => { const id = p.profile || '__none__'; m[id] = (m[id] || 0) + 1; });
    return m;
  }, [posts]);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (groupFilter !== 'all') {
        if (groupFilter === '__none__' ? p.profile : p.profile !== groupFilter) return false;
      }
      if (typeFilter !== 'all' && mediaTypeOf(p) !== typeFilter) return false;
      if (search.trim()) {
        const hay = `${p.message || ''}`.toLowerCase();
        if (!hay.includes(search.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [posts, groupFilter, typeFilter, search]);

  const allSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((p) => p.id)));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleBulkAssign = async (groupId) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => axios.put(`${API}/api/drafts/${id}`, { profile: groupId })));
      notify(`${ids.length} post(s) assigné(s)${groupId ? '' : ' (sans groupe)'}`);
      setSelectedIds(new Set());
      fetchAll();
    } catch (e) {
      notify("Erreur lors de l'assignation", 'error');
    }
  };

  const handleDelete = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Supprimer ${ids.length} post(s) ?`)) return;
    try {
      await Promise.all(ids.map((id) => axios.delete(`${API}/api/drafts/${id}`)));
      notify(`${ids.length} post(s) supprimé(s)`);
      setSelectedIds(new Set());
      if (preview && ids.includes(preview.id)) setPreview(null);
      fetchAll();
    } catch (e) {
      notify('Erreur lors de la suppression', 'error');
    }
  };

  // ---- CSV import : colonnes caption[,group] -----------------------------
  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) { notify('CSV vide', 'error'); return; }
      // détection d'en-tête
      const header = rows[0].map((c) => c.toLowerCase().trim());
      const hasHeader = header.includes('caption') || header.includes('message') || header.includes('group');
      const capIdx = hasHeader ? header.findIndex((h) => h === 'caption' || h === 'message') : 0;
      const grpIdx = hasHeader ? header.findIndex((h) => h === 'group' || h === 'profile') : 1;
      const body = hasHeader ? rows.slice(1) : rows;
      const drafts = body
        .map((r) => ({
          message: (r[capIdx >= 0 ? capIdx : 0] || '').trim(),
          profile: grpIdx >= 0 ? (r[grpIdx] || '').trim() : '',
          mediaType: 'text',
        }))
        .filter((d) => d.message);
      if (!drafts.length) { notify('Aucune légende trouvée dans le CSV', 'error'); return; }
      const res = await axios.post(`${API}/api/drafts/bulk`, { drafts });
      notify(`${res.data.added} post(s) importé(s)${res.data.failed ? `, ${res.data.failed} ignoré(s)` : ''}`);
      fetchAll();
    } catch (err) {
      notify('Erreur import CSV', 'error');
    }
  };

  const openNew = () => { setEditingPost(null); setComposerOpen(true); };
  const openEdit = (post) => { setEditingPost(post); setComposerOpen(true); };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', gap: 2.5, height: 'calc(100vh - 0px)' }}>
      {/* ---------------- main column ---------------- */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', color: t.mutedFaint, textTransform: 'uppercase' }}>
              Library
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
                Post Library
              </Typography>
              <Chip
                label={posts.length}
                size="small"
                sx={{ bgcolor: alpha(t.primary, 0.15), color: '#c7d2fe', fontWeight: 700, height: 22, border: `1px solid ${alpha(t.primary, 0.3)}` }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Rechercher…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
              sx={{ width: 240, '& .MuiOutlinedInput-root': { bgcolor: t.card } }}
            />
            <input ref={csvInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleCsv} />
            <Button variant="outlined" color="inherit" startIcon={<UploadIcon sx={{ fontSize: 18 }} />} onClick={() => csvInputRef.current?.click()}
              sx={{ borderColor: t.border, color: t.muted }}>
              Import .csv
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
              New
            </Button>
          </Box>
        </Box>

        {/* group tabs + type filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <GroupTab active={groupFilter === 'all'} onClick={() => setGroupFilter('all')} label="All" count={posts.length} />
            {Object.entries(groups).map(([id, g]) => (
              <GroupTab key={id} active={groupFilter === id} onClick={() => setGroupFilter(id)}
                label={g.name || id} count={groupCounts[id] || 0} color={g.color} dot />
            ))}
            {groupCounts['__none__'] > 0 && (
              <GroupTab active={groupFilter === '__none__'} onClick={() => setGroupFilter('__none__')}
                label="Sans groupe" count={groupCounts['__none__']} />
            )}
          </Box>
          <ToggleButtonGroup
            size="small" exclusive value={typeFilter}
            onChange={(_, v) => v && setTypeFilter(v)}
            sx={{
              bgcolor: t.card, borderRadius: 2, p: 0.4,
              '& .MuiToggleButton-root': { border: 0, borderRadius: '6px !important', px: 1.25, py: 0.4, color: t.muted, textTransform: 'none', fontSize: '0.78rem' },
              '& .Mui-selected': { bgcolor: `${alpha(t.primary, 0.18)} !important`, color: '#c7d2fe !important' },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="text"><TextIcon sx={{ fontSize: 16 }} /></ToggleButton>
            <ToggleButton value="image"><ImageIcon sx={{ fontSize: 16 }} /></ToggleButton>
            <ToggleButton value="video"><MovieIcon sx={{ fontSize: 16 }} /></ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* bulk action bar */}
        {selectedIds.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha(t.primary, 0.08), border: `1px solid ${alpha(t.primary, 0.25)}` }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{selectedIds.size} sélectionné(s)</Typography>
            <Button size="small" variant="outlined" startIcon={<FolderIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => setAssignAnchor(e.currentTarget)} sx={{ borderColor: t.border, color: t.foreground }}>
              Assigner à un groupe
            </Button>
            <Button size="small" color="error" startIcon={<DeleteIcon sx={{ fontSize: 18 }} />} onClick={() => handleDelete([...selectedIds])}>
              Supprimer
            </Button>
            <Button size="small" color="inherit" sx={{ color: t.muted }} onClick={() => setSelectedIds(new Set())}>Annuler</Button>
          </Box>
        )}

        {/* menu d'assignation de groupe en masse */}
        <Menu anchorEl={assignAnchor} open={Boolean(assignAnchor)} onClose={() => setAssignAnchor(null)}>
          {Object.entries(groups).map(([id, g]) => (
            <MenuItem key={id} onClick={() => { setAssignAnchor(null); handleBulkAssign(id); }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: g.color || t.mutedFaint, mr: 1 }} />
              {g.name || id}
            </MenuItem>
          ))}
          {Object.keys(groups).length === 0 && (
            <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>Crée un groupe d'abord</Typography></MenuItem>
          )}
          <MenuItem onClick={() => { setAssignAnchor(null); handleBulkAssign(''); }} sx={{ color: t.muted }}>
            <CloseIcon sx={{ fontSize: 16, mr: 1 }} /> Sans groupe
          </MenuItem>
        </Menu>

        {/* table */}
        <Box sx={{ flex: 1, minHeight: 0, border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* table head */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: `1px solid ${t.border}`, color: t.mutedFaint, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selectedIds.size > 0} onChange={toggleAll} sx={{ p: 0.5, mr: 1 }} />
            <Box sx={{ flex: 1 }}>Caption</Box>
            <Box sx={{ width: 120 }}>Group</Box>
            <Box sx={{ width: 60, textAlign: 'right' }}>Used</Box>
            <Box sx={{ width: 70, textAlign: 'right' }}>Interac.</Box>
            <Box sx={{ width: 60, textAlign: 'right' }}>Likes</Box>
            <Box sx={{ width: 70, textAlign: 'right' }}>Date</Box>
            <Box sx={{ width: 40 }} />
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}><CircularProgress size={26} /></Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: t.mutedFaint }}>
                <FolderIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
                <Typography sx={{ fontSize: '0.9rem' }}>Aucun post. Clique sur « New » pour en créer un.</Typography>
              </Box>
            ) : (
              filtered.map((post) => {
                const isSel = selectedIds.has(post.id);
                const isPrev = preview?.id === post.id;
                return (
                  <Box key={post.id} onClick={() => setPreview(post)}
                    sx={{
                      display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer',
                      borderBottom: `1px solid ${alpha(t.border, 0.6)}`,
                      bgcolor: isPrev ? alpha(t.primary, 0.07) : 'transparent',
                      transition: 'background-color .12s', '&:hover': { bgcolor: alpha('#fff', 0.025) },
                    }}>
                    <Checkbox size="small" checked={isSel} onClick={(e) => e.stopPropagation()} onChange={() => toggleOne(post.id)} sx={{ p: 0.5, mr: 1 }} />
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <ThumbStack post={post} size={34} />
                      <Typography noWrap sx={{ fontSize: '0.85rem', color: t.foreground, maxWidth: '100%' }}>
                        {post.message || <span style={{ color: t.mutedFaint }}>— sans texte —</span>}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 120 }}><GroupChip group={groupOf(post)} /></Box>
                    <Box sx={{ width: 60, textAlign: 'right' }}><Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{post.used_count ?? 0}</Typography></Box>
                    <Box sx={{ width: 70, textAlign: 'right' }}><Typography sx={{ fontSize: '0.82rem', color: t.muted }}>{post.engaged_posts ? ((post.replies || 0) + (post.reposts || 0) + (post.quotes || 0)).toLocaleString('fr-FR') : '—'}</Typography></Box>
                    <Box sx={{ width: 60, textAlign: 'right' }}><Typography sx={{ fontSize: '0.82rem', color: post.likes ? '#f9a8d4' : t.muted, fontWeight: post.likes ? 600 : 400 }}>{post.likes != null ? post.likes.toLocaleString('fr-FR') : '—'}</Typography></Box>
                    <Box sx={{ width: 70, textAlign: 'right' }}><Typography sx={{ fontSize: '0.78rem', color: t.mutedFaint }}>{fmtDate(post.createdAt)}</Typography></Box>
                    <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(post); }} sx={{ color: t.mutedFaint }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      {/* ---------------- preview panel ---------------- */}
      <Box sx={{ width: 360, flexShrink: 0, border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column' }}>
        {preview ? (
          <PreviewPanel post={preview} group={groupOf(preview)} onEdit={() => openEdit(preview)} onDelete={() => handleDelete([preview.id])} />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: t.mutedFaint, gap: 1.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: 3, bgcolor: alpha(t.primary, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChatIcon sx={{ fontSize: 28, color: alpha(t.primary, 0.7) }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: t.muted }}>Sélectionne un post</Typography>
            <Typography sx={{ fontSize: '0.8rem' }}>Clique pour voir l'aperçu</Typography>
          </Box>
        )}
      </Box>

      {/* ---------------- composer ---------------- */}
      <PostComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        groups={groups}
        editingPost={editingPost}
        onSaved={() => { setComposerOpen(false); fetchAll(); notify(editingPost ? 'Post mis à jour' : 'Post créé'); }}
        onError={(m) => notify(m, 'error')}
      />

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((s) => ({ ...s, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

// ---- group tab ----------------------------------------------------------
function GroupTab({ active, onClick, label, count, color, dot }) {
  return (
    <Box onClick={onClick} role="button"
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.6, borderRadius: 2, cursor: 'pointer', userSelect: 'none',
        bgcolor: active ? alpha('#fff', 0.07) : 'transparent',
        border: `1px solid ${active ? t.border : 'transparent'}`,
        '&:hover': { bgcolor: alpha('#fff', 0.04) },
      }}>
      {dot && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color || t.primary }} />}
      <Typography sx={{ fontSize: '0.82rem', fontWeight: active ? 600 : 500, color: active ? t.foreground : t.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.74rem', color: t.mutedFaint }}>{count}</Typography>
    </Box>
  );
}

// ---- preview panel ------------------------------------------------------
function PreviewPanel({ post, group, onEdit, onDelete }) {
  const medias = mediaUrls(post);
  const interac = post.engaged_posts ? (post.replies || 0) + (post.reposts || 0) + (post.quotes || 0) : null;
  const stats = [
    { label: 'Utilisé', value: post.used_count ?? 0 },
    { label: 'Likes', value: post.likes != null ? post.likes.toLocaleString('fr-FR') : '—' },
    { label: 'Interactions', value: interac != null ? interac.toLocaleString('fr-FR') : '—' },
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Aperçu</Typography>
        <Box>
          <Tooltip title="Éditer"><IconButton size="small" onClick={onEdit} sx={{ color: t.muted }}><EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Supprimer"><IconButton size="small" onClick={onDelete} sx={{ color: '#f87171' }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        </Box>
      </Box>
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {medias.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: medias.length > 1 ? '1fr 1fr' : '1fr', gap: 1, mb: 2 }}>
            {medias.map((m, i) => (
              <Box key={i} component={m.video ? 'video' : 'img'} src={m.url} controls={m.video}
                onError={(e) => { e.target.style.display = 'none'; }}
                sx={{ width: '100%', borderRadius: 2, border: `1px solid ${t.border}`, maxHeight: medias.length > 1 ? 160 : 280, objectFit: 'cover' }} />
            ))}
          </Box>
        )}
        {post.randomFolder > 0 && (
          <Box sx={{ mb: 2, px: 1.25, py: 0.85, borderRadius: 1.5, bgcolor: alpha('#f43f5e', 0.12), border: '1px solid rgba(244,63,94,0.3)' }}>
            <Typography sx={{ fontSize: '0.8rem', color: '#fca5a5', fontWeight: 600 }}>🎲 {post.randomCount || 1} image(s) tirée(s) au hasard{post.randomName ? ` · ${post.randomName}` : ''}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint }}>Choisies à la publication</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.5, mb: 2 }}>
          {post.message || <span style={{ color: t.mutedFaint }}>— sans texte —</span>}
        </Typography>
        <Box sx={{ mb: 2 }}><GroupChip group={group} /></Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {stats.map((s) => (
            <Box key={s.label} sx={{ flex: 1, p: 1.25, borderRadius: 2, bgcolor: alpha('#fff', 0.03), border: `1px solid ${t.border}`, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: t.mutedFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ---- composer (New / Edit) ---------------------------------------------
function PostComposer({ open, onClose, groups, editingPost, onSaved, onError }) {
  const [message, setMessage] = useState('');
  const [groupId, setGroupId] = useState('');
  const [images, setImages] = useState([]); // {id, preview}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupMenu, setGroupMenu] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [randomSource, setRandomSource] = useState(null); // {folder, count, name}
  const textRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMessage(editingPost?.message || '');
      setGroupId(editingPost?.profile || '');
      // pré-remplir les images existantes du brouillon (preview via le backend)
      const ids = editingPost?.imageUploadIds || editingPost?.uploadIds || [];
      setImages(Array.isArray(ids) ? ids.map((id) => ({ id, preview: `${API}/api/uploads/${id}/raw?token=${getToken()}` })) : []);
      setRandomSource(editingPost?.randomFolder ? { folder: editingPost.randomFolder, count: editingPost.randomCount || 1, name: editingPost.randomName || 'dossier' } : null);
    }
  }, [open, editingPost]);

  const handleMediaAdd = ({ uploads, random }) => {
    if (uploads && uploads.length) { setRandomSource(null); setImages((p) => [...p, ...uploads]); }
    if (random) { setImages([]); setRandomSource(random); }
  };

  const insertVar = (key) => {
    const el = textRef.current;
    const token = `{${key}}`;
    if (!el) { setMessage((m) => m + token); return; }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    const next = message.slice(0, start) + token + message.slice(end);
    setMessage(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const res = await axios.post(`${API}/api/upload`, fd);
        setImages((prev) => [...prev, { id: res.data.uploadId, preview: URL.createObjectURL(file) }]);
      }
    } catch (err) {
      onError(err.response?.data?.error || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!message.trim() && images.length === 0 && !randomSource) { onError('Le post est vide'); return; }
    setSaving(true);
    const media = {
      imageUploadIds: images.map((i) => i.id),
      mediaType: (images.length || randomSource) ? 'image' : 'text',
      ...(randomSource ? { randomFolder: randomSource.folder, randomCount: randomSource.count, randomName: randomSource.name } : { randomFolder: 0 }),
    };
    try {
      if (editingPost) {
        await axios.put(`${API}/api/drafts/${editingPost.id}`, { message, profile: groupId, ...media });
      } else {
        await axios.post(`${API}/api/drafts`, {
          message,
          profile: groupId,
          ...media,
          title: `Post ${new Date().toLocaleString()}`,
        });
      }
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const selectedGroup = groupId ? (groups[groupId] || { name: groupId }) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, borderRadius: 3 } }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700 }}>{editingPost ? 'Éditer le post' : 'Nouveau post'}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: t.muted }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 0 }}>
        {/* editor */}
        <Box sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, p: 1.5, bgcolor: t.background }}>
          <TextField
            inputRef={textRef}
            multiline minRows={5} maxRows={12} fullWidth variant="standard"
            placeholder="Écris ta légende… utilise les variables ci-dessous"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem' } }}
          />
          {/* images preview */}
          {images.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {images.map((img, i) => (
                <Box key={i} sx={{ position: 'relative' }}>
                  <Box component="img" src={img.preview} sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: 'cover', border: `1px solid ${t.border}` }} />
                  <IconButton size="small" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: t.background, border: `1px solid ${t.border}`, p: 0.25, '&:hover': { bgcolor: '#000' } }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Divider sx={{ my: 1.25, borderColor: t.border }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleUpload} />
              <Tooltip title="Ajouter des images ou vidéos">
                <IconButton size="small" onClick={() => setPickerOpen(true)} sx={{ color: t.muted }}>
                  {uploading ? <CircularProgress size={18} /> : <AddPhotoIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Tooltip>
              {randomSource && (
                <Chip size="small" label={`🎲 ${randomSource.count} de "${randomSource.name}"`} onDelete={() => setRandomSource(null)}
                  sx={{ bgcolor: alpha('#f43f5e', 0.15), color: '#fca5a5', border: '1px solid rgba(244,63,94,0.3)' }} />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: message.length > MAX_CHARS - 50 ? '#fbbf24' : t.mutedFaint }}>
              {MAX_CHARS - message.length}
            </Typography>
          </Box>
        </Box>

        {/* variables */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <VarIcon sx={{ fontSize: 15, color: t.mutedFaint }} />
            <Typography sx={{ fontSize: '0.78rem', color: t.muted, fontWeight: 600 }}>Variables</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {VARIABLES.map((v) => (
              <Chip key={v.key} label={v.label} size="small" onClick={() => insertVar(v.key)}
                sx={{ bgcolor: alpha('#fff', 0.04), border: `1px solid ${t.border}`, color: t.muted, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', cursor: 'pointer', '&:hover': { bgcolor: alpha(t.primary, 0.15), color: '#c7d2fe' } }} />
            ))}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: t.mutedFaint, mt: 1 }}>
            Résolues automatiquement à la publication selon le compte cible.
          </Typography>
        </Box>

        {/* group selector */}
        <Box sx={{ mt: 2.5 }}>
          <Button variant="outlined" color="inherit" startIcon={<FolderIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setGroupMenu(e.currentTarget)}
            sx={{ borderColor: t.border, color: selectedGroup ? t.foreground : t.muted, textTransform: 'none' }}>
            {selectedGroup ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {selectedGroup.color && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: selectedGroup.color }} />}
                {selectedGroup.name}
              </Box>
            ) : 'Groupe'}
          </Button>
          <Menu anchorEl={groupMenu} open={!!groupMenu} onClose={() => setGroupMenu(null)}
            PaperProps={{ sx: { bgcolor: t.card, border: `1px solid ${t.border}`, minWidth: 200 } }}>
            <MenuItem onClick={() => { setGroupId(''); setGroupMenu(null); }}>
              <Typography sx={{ color: t.muted }}>Sans groupe</Typography>
            </MenuItem>
            {Object.entries(groups).map(([id, g]) => (
              <MenuItem key={id} onClick={() => { setGroupId(id); setGroupMenu(null); }} selected={id === groupId}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color || t.primary }} />
                  {g.name || id}
                </Box>
              </MenuItem>
            ))}
            {Object.keys(groups).length === 0 && (
              <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: t.mutedFaint }}>Crée un groupe dans « Profils de comptes »</Typography></MenuItem>
            )}
          </Menu>
        </Box>
      </DialogContent>
      <Box sx={{ p: 2.5, pt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: t.muted }}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
          {saving ? <CircularProgress size={20} /> : editingPost ? 'Enregistrer' : 'Créer le post'}
        </Button>
      </Box>

      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onAdd={handleMediaAdd} used={images.length} />
    </Dialog>
  );
}

// ---- tiny CSV parser (handles quotes & commas) -------------------------
function parseCsv(text) {
  const rows = [];
  let row = [], cur = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; } else inQuotes = false;
      } else cur += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(cur); cur = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      if (cur !== '' || row.length) { row.push(cur); rows.push(row); row = []; cur = ''; }
    } else cur += c;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}
